import { CLASS_NAMES } from "@/core/constants/class-names";
import { formatDate } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { IReview } from "@/reviews/review";
import {
  SPIFF_RULE_EVENTS,
  SPIFF_RULE_EVENTS_LABELS,
} from "@/spiff-rules/constants";
import { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { BaseSpiff, ISpiff, CustomerTrigger } from "./spiffs.d";

export const getEnrichedSpiff = (spiff: ISpiff): ISpiff => ({
  ...spiff,
  user_id: spiff.user_id,
  display_identification:
    spiff.trigger_event === SPIFF_RULE_EVENTS.yard_sign_photo
      ? "Yard sign photo"
      : spiff.display_identification,
});

export const getEnrichedBaseSpiff = (spiff: BaseSpiff): BaseSpiff => ({
  ...spiff,
  user_id: spiff.user_id,
  display_identification:
    spiff.trigger_event === SPIFF_RULE_EVENTS.yard_sign_photo
      ? "Yard sign photo"
      : spiff.display_identification,
});

export interface FilterableSpiff extends ISpiff {
  searchableCustomerName: string;
  searchableEventType: string;
  searchableDate: string;
  searchableAmount: string;
}

/**
 * Enriches a spiff with searchable properties for filtering and searching.
 * This should be called when spiffs are fetched from the backend.
 */
export const enrichSpiffForFiltering = (spiff: ISpiff): FilterableSpiff => {
  const trigger = spiff.trigger;
  const isSignup = spiff.trigger_event === SPIFF_RULE_EVENTS.signup;

  // Get customer name based on trigger type
  let customerName = "";
  if (isSignup && trigger?.class_name === CLASS_NAMES.Customer) {
    customerName = getCustomerNameOrPhone(trigger as CustomerTrigger);
  } else if (spiff.customer) {
    customerName = getCustomerNameOrPhone(spiff.customer);
  } else if (trigger?.class_name === CLASS_NAMES.Review) {
    const reviewTrigger = trigger as IReview;
    customerName = getCustomerNameOrPhone(reviewTrigger.customer);
    if (customerName === "-") {
      customerName = reviewTrigger.reviewer_name;
    }
  }

  // Get created_at from trigger, falling back to spiff.created_at
  const triggerDate =
    trigger && "created_at" in trigger ? trigger.created_at : spiff.created_at;

  return {
    ...spiff,
    searchableCustomerName: customerName || "",
    searchableEventType:
      SPIFF_RULE_EVENTS_LABELS[spiff.trigger_event as SpiffRuleEvent] ||
      spiff.trigger_event,
    searchableDate: formatDate(triggerDate),
    searchableAmount: formatMoney(spiff.amount) || "",
  };
};
